import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "@/lib/firebase";
import { collection, getDocs, query, where, updateDoc, doc, getDoc } from "firebase/firestore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, Filter } from "lucide-react";

const StaffDashboard: React.FC = () => {
  const [branch, setBranch] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [allEnrollments, setAllEnrollments] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string>("");
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [monthFilter, setMonthFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showFilters, setShowFilters] = useState<boolean>(false);

  useEffect(() => {
    const fetchBranchAndEnrollments = async () => {
      if (!auth.currentUser) return;
      // Fetch staff user profile to get branch
      const usersSnap = await getDocs(query(collection(db, "users"), where("uid", "==", auth.currentUser.uid)));
      const userDoc = usersSnap.docs[0]?.data();
      if (userDoc && userDoc.branch) {
        setBranchId(userDoc.branch);
        // Fetch branch name from branches collection
        const branchDoc = await getDoc(doc(db, "branches", userDoc.branch));
        const branchData = branchDoc.data();
        setBranch(branchData?.name || userDoc.branch);
        // Fetch all enrollments for this branch (not just pending)
        const enrollSnap = await getDocs(query(collection(db, "enrollments"), where("branch", "==", userDoc.branch)));
        setAllEnrollments(enrollSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        // Fetch all courses
        const coursesSnap = await getDocs(collection(db, "courses"));
        setCourses(coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        // Fetch all students
        const studentsSnap = await getDocs(collection(db, "users"));
        setStudents(studentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
      setLoading(false);
    };
    fetchBranchAndEnrollments();
  }, []);

  const updateStatus = async (enrollmentId: string, status: string) => {
    try {
      await updateDoc(doc(db, "enrollments", enrollmentId), { status });
      setMessage(`Enrollment ${status}`);
      setAllEnrollments(enrollments => enrollments.map(e => e.id === enrollmentId ? { ...e, status } : e));
    } catch (err: any) {
      setMessage("Failed to update status: " + err.message);
    }
  };

  // Get available years from enrollments
  const availableYears = useMemo(() => {
    const years = new Set<number>();
    allEnrollments.forEach(enrollment => {
      if (enrollment.enrolledAt && enrollment.enrolledAt.seconds) {
        const year = new Date(enrollment.enrolledAt.seconds * 1000).getFullYear();
        years.add(year);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [allEnrollments]);

  // Filter enrollments based on selected filters
  const filteredEnrollments = useMemo(() => {
    return allEnrollments.filter(enrollment => {
      // Status filter
      if (statusFilter !== "all" && enrollment.status !== statusFilter) {
        return false;
      }

      // Date filters
      if (enrollment.enrolledAt && enrollment.enrolledAt.seconds) {
        const enrollDate = new Date(enrollment.enrolledAt.seconds * 1000);
        
        // Month filter
        if (monthFilter !== "all") {
          const enrollMonth = enrollDate.getMonth();
          if (enrollMonth !== parseInt(monthFilter)) {
            return false;
          }
        }

        // Year filter
        if (yearFilter !== "all") {
          const enrollYear = enrollDate.getFullYear();
          if (enrollYear !== parseInt(yearFilter)) {
            return false;
          }
        }
      }

      // Search filter
      if (searchTerm) {
        const student = students.find(s => s.id === enrollment.studentId);
        const course = courses.find(c => c.id === enrollment.courseId);
        const searchLower = searchTerm.toLowerCase();
        
        const studentName = student ? `${student.firstName} ${student.lastName}`.toLowerCase() : "";
        const studentEmail = student ? student.email.toLowerCase() : "";
        const courseName = course ? course.title.toLowerCase() : "";
        
        if (!studentName.includes(searchLower) && 
            !studentEmail.includes(searchLower) && 
            !courseName.includes(searchLower) &&
            !enrollment.id.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [allEnrollments, statusFilter, monthFilter, yearFilter, searchTerm, students, courses]);

  const clearFilters = () => {
    setStatusFilter("all");
    setMonthFilter("all");
    setYearFilter("all");
    setSearchTerm("");
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Staff Dashboard</h1>
      </div>
      {message && <div className="mb-4 p-3 bg-blue-100 border border-blue-300 text-blue-800 rounded">{message}</div>}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-lg">Loading...</div>
        </div>
      ) : !branch ? (
        <div className="p-4 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded">
          No branch assigned to your account.
        </div>
      ) : (
        <>
          <div className="mb-6 p-4 bg-gray-100 rounded">
            <span className="text-lg">Branch: <b>{branch}</b></span>
          </div>

          {/* Filters Section */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Enrollment Filters</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </div>

            {showFilters && (
              <div className="p-4 border rounded-lg bg-white space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  {/* Search */}
                  <div className="lg:col-span-2">
                    <label className="text-sm font-medium mb-2 block">Search Student</label>
                    <div className="relative">
                      <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Search by name, email, or course..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Status</label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Month Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Month</label>
                    <Select value={monthFilter} onValueChange={setMonthFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Months" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Months</SelectItem>
                        <SelectItem value="0">January</SelectItem>
                        <SelectItem value="1">February</SelectItem>
                        <SelectItem value="2">March</SelectItem>
                        <SelectItem value="3">April</SelectItem>
                        <SelectItem value="4">May</SelectItem>
                        <SelectItem value="5">June</SelectItem>
                        <SelectItem value="6">July</SelectItem>
                        <SelectItem value="7">August</SelectItem>
                        <SelectItem value="8">September</SelectItem>
                        <SelectItem value="9">October</SelectItem>
                        <SelectItem value="10">November</SelectItem>
                        <SelectItem value="11">December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Year Filter */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Year</label>
                    <Select value={yearFilter} onValueChange={setYearFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Years" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Years</SelectItem>
                        {availableYears.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-2">
                  <div className="text-sm text-gray-600">
                    Showing {filteredEnrollments.length} of {allEnrollments.length} enrollments
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    disabled={statusFilter === "all" && monthFilter === "all" && yearFilter === "all" && !searchTerm}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Enrollments</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredEnrollments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {allEnrollments.length === 0 
                    ? "No enrollments for your branch."
                    : "No enrollments match the current filters."
                  }
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 p-3 text-left">Student</th>
                        <th className="border border-gray-300 p-3 text-left">Course</th>
                        <th className="border border-gray-300 p-3 text-left">Status</th>
                        <th className="border border-gray-300 p-3 text-left">Enrolled Date</th>
                        <th className="border border-gray-300 p-3 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEnrollments.map(e => {
                        const student = students.find(s => s.id === e.studentId);
                        const course = courses.find(c => c.id === e.courseId);
                        return (
                          <tr key={e.id} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-3">
                              {student ? `${student.firstName} ${student.lastName}` : e.studentId}
                              {student && (
                                <div className="text-sm text-gray-500">{student.email}</div>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3">
                              {course ? course.title : e.courseId}
                              {course && (
                                <div className="text-sm text-gray-500">₱{course.price}</div>
                              )}
                            </td>
                            <td className="border border-gray-300 p-3">
                              <span className={`inline-block px-2 py-1 rounded text-sm ${
                                e.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                e.status === 'approved' ? 'bg-green-100 text-green-800' :
                                e.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                e.status === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                e.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {e.status}
                              </span>
                            </td>
                            <td className="border border-gray-300 p-3">
                              {e.enrolledAt ? new Date(e.enrolledAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                            </td>
                            <td className="border border-gray-300 p-3">
                              {e.status === 'pending' ? (
                                <div className="space-x-2">
                                  <button 
                                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                                    onClick={() => updateStatus(e.id, "approved")}
                                  >
                                    Approve
                                  </button>
                                  <button 
                                    className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
                                    onClick={() => updateStatus(e.id, "rejected")}
                                  >
                                    Reject
                                  </button>
                                </div>
                              ) : (
                                <span className="text-sm text-gray-500">No actions available</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default StaffDashboard;
